import {
    createNdjsonParser,
    type NdjsonParseProblem,
    type NdjsonParseResult,
    type StreamLineClassifier,
} from '@apps/web/features/phoebe_chat_v3/api/ndjson_parser';
import {
    RunEventStreamHttpError,
    RunEventStreamProtocolError,
} from '@apps/web/features/phoebe_chat_v3/api/run_api';
import {
    type DoneLine,
    type EventStreamLine,
    type HarnessScope,
    type StreamCursor,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type ProtocolProblem } from '@apps/web/features/phoebe_chat_v3/model/state';
import { type ForegroundClock } from '@apps/web/features/phoebe_chat_v3/model/transcript_store';
import { type StreamRequest } from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';

const ORIGIN_CURSOR: StreamCursor = {
    after_offset: -1,
    after_pending_created_at: null,
    after_pending_id: null,
    after_pending_status_updated_at: null,
    after_pending_status_id: null,
};
const MAX_DELAY_MS = 30_000;
const NETWORK_RETRY_PLATEAU_MS = 5_000;
const NETWORK_RETRY_BASES_MS = [
    500,
    1_000,
    2_000,
    NETWORK_RETRY_PLATEAU_MS,
] as const;
const PROTOCOL_REPLAY_DELAY_MS = 1_000;
const MIN_RETRY_AFTER_MS = 1_000;
const MAX_RETRY_AFTER_MS = 60_000;
type DataLine = Exclude<EventStreamLine, DoneLine>;
export type StreamActivityState = 'active' | 'waiting' | 'terminal' | 'idle';
export type EventStreamCycleState =
    | 'draining'
    | 'complete'
    | 'rate-limited'
    | 'retrying'
    | 'hidden'
    | 'replaying'
    | 'stopped';
export type EventStreamHttpFailureReason =
    | 'rate_limited'
    | 'unauthorized'
    | 'forbidden'
    | 'not_found'
    | 'unprocessable_entity'
    | 'client_error'
    | 'request_timeout'
    | 'server_error';
export type EventStreamClientFailure =
    | Readonly<{
          readonly kind: 'transport';
          readonly reason: 'open_failed' | 'read_failed';
          readonly acknowledgedCursor: StreamCursor;
      }>
    | Readonly<{
          readonly kind: 'parser';
          readonly reason: NdjsonParseProblem['kind'];
          readonly acknowledgedCursor: StreamCursor;
      }>
    | Readonly<{
          readonly kind: 'protocol';
          readonly reason: ProtocolProblem['reason'];
          readonly lineType: string | null;
          readonly eventType: string | null;
          readonly acknowledgedCursor: StreamCursor;
      }>
    | Readonly<{
          readonly kind: 'http';
          readonly reason: EventStreamHttpFailureReason;
          readonly status: number;
          readonly acknowledgedCursor: StreamCursor;
      }>;
type ProtocolFailure = Extract<
    EventStreamClientFailure,
    { readonly kind: 'protocol' }
>;
export type EventStreamIngestionResult =
    | Readonly<{
          readonly ok: true;
          readonly acknowledgedCursor: StreamCursor;
          readonly runStateChanged: boolean;
      }>
    | Readonly<{
          readonly ok: false;
          readonly problem: Readonly<{
              readonly reason: ProtocolProblem['reason'];
              readonly lineType: string | null;
              readonly eventType: string | null;
          }>;
      }>;
type CancelScheduledTask = () => void;
type Schedule = (callback: () => void, delayMs: number) => CancelScheduledTask;
type UnsubscribeBrowserState = () => void;
type SubscribeBrowserState = (listener: () => void) => UnsubscribeBrowserState;
export type EventStreamClientOptions = Readonly<{
    readonly scope: HarnessScope;
    readonly classifyLine: StreamLineClassifier;
    readonly openEventStream: (request: StreamRequest) => Promise<Response>;
    readonly ingestLine: (line: DataLine) => EventStreamIngestionResult;
    readonly publishCycle: () => void;
    readonly onCycleComplete: () => void;
    readonly recordIssue: (failure: EventStreamClientFailure) => void;
    readonly handoffFailure: (failure: EventStreamClientFailure) => void;
    readonly schedule: Schedule;
    readonly queueMicrotask: (callback: () => void) => CancelScheduledTask;
    readonly jitterFactor: () => number;
    readonly getActivityState: () => StreamActivityState;
    /** Reports visible time to every consumer sharing this harness clock. */
    readonly foregroundClock: ForegroundClock;
    /** Reads the current document visibility without using browser globals. */
    readonly getVisible: () => boolean;
    /** Subscribes to document visibility changes for this mounted harness. */
    readonly subscribeVisibility: SubscribeBrowserState;
    /** Reads the browser's connectivity hint without using browser globals. */
    readonly getOnline: () => boolean;
    /** Subscribes to browser connectivity changes for this mounted harness. */
    readonly subscribeOnline: SubscribeBrowserState;
    /** Publishes the finite transport state without owning UI presentation. */
    readonly setCycleState: (state: EventStreamCycleState) => void;
}>;
export type EventStreamClient = Readonly<{
    readonly start: () => void;
    readonly stop: () => void;
}>;
function cursorParts(cursor: StreamCursor): readonly [number, string, string] {
    return [
        cursor.after_offset,
        `${cursor.after_pending_created_at ?? ''}\u0000${cursor.after_pending_id ?? ''}`,
        `${cursor.after_pending_status_updated_at ?? ''}\u0000${cursor.after_pending_status_id ?? ''}`,
    ];
}
function compareCursor(left: StreamCursor, right: StreamCursor): Array<number> {
    const leftParts = cursorParts(left);
    const rightParts = cursorParts(right);
    return leftParts.map((part, index) => {
        const other = rightParts[index];
        return typeof part === 'number' && typeof other === 'number'
            ? Math.sign(part - other)
            : String(part).localeCompare(String(other));
    });
}
function doneCursorProblem(
    actual: StreamCursor,
    expected: StreamCursor,
): 'cursor_regression' | 'cursor_jump' | null {
    const comparisons = compareCursor(actual, expected);
    if (comparisons.every((comparison) => comparison === 0)) {
        return null;
    }
    return comparisons.some((comparison) => comparison < 0)
        ? 'cursor_regression'
        : 'cursor_jump';
}
function activityBaseMs(state: StreamActivityState): number {
    switch (state) {
        case 'active':
            return 250;
        case 'waiting':
            return 4_000;
        case 'terminal':
        case 'idle':
            return 15_000;
        default: {
            const exhaustiveState: never = state;
            throw new Error(
                `Unknown event stream activity state: ${String(exhaustiveState)}`,
            );
        }
    }
}
function protocolFailure(
    reason: ProtocolProblem['reason'],
    acknowledgedCursor: StreamCursor,
    lineType: string | null = null,
    eventType: string | null = null,
): ProtocolFailure {
    return {
        kind: 'protocol',
        reason,
        lineType,
        eventType,
        acknowledgedCursor,
    };
}

type ProtocolFingerprint = readonly [
    ProtocolProblem['reason'],
    string | null,
    string | null,
    number,
    string | null,
    string | null,
    string | null,
    string | null,
];
type HttpFailurePolicy = Readonly<{
    readonly reason: EventStreamHttpFailureReason;
    readonly recovery: 'rate_limited' | 'retry' | 'stop';
}>;

function httpFailurePolicy(status: number): HttpFailurePolicy {
    switch (status) {
        case 401:
            return { reason: 'unauthorized', recovery: 'stop' };
        case 403:
            return { reason: 'forbidden', recovery: 'stop' };
        case 404:
            return { reason: 'not_found', recovery: 'stop' };
        case 408:
            return { reason: 'request_timeout', recovery: 'retry' };
        case 422:
            return { reason: 'unprocessable_entity', recovery: 'stop' };
        case 429:
            return { reason: 'rate_limited', recovery: 'rate_limited' };
        default:
            if (status >= 500 && status < 600) {
                return { reason: 'server_error', recovery: 'retry' };
            }
            return { reason: 'client_error', recovery: 'stop' };
    }
}

type ActiveCycleReadState =
    | 'opening'
    | 'reading_without_done'
    | 'done_before_eof'
    | 'eof_proven';

/**
 * Owns one finite long-poll read and its recovery policy for a mounted harness.
 * Browser state is injected so tests and non-window hosts drive lifecycle
 * transitions without hidden global dependencies.
 */
export function createEventStreamClient(
    options: EventStreamClientOptions,
): EventStreamClient {
    let running = false;
    let stoppedByFailure = false;
    let activeController: AbortController | null = null;
    let activeCycleReadState: ActiveCycleReadState = 'opening';
    let cancelScheduledTask: CancelScheduledTask | null = null;
    let unsubscribeVisibility: UnsubscribeBrowserState | null = null;
    let unsubscribeOnline: UnsubscribeBrowserState | null = null;
    let scheduleVersion = 0;
    let acknowledgedCursor = ORIGIN_CURSOR;
    let publishedCursor = ORIGIN_CURSOR;
    let firstRequest = true;
    let consecutiveNoProgressCount = 0;
    let networkRetryIndex = 0;
    let replayedProtocolFingerprint: ProtocolFingerprint | null = null;
    let visible = true;
    let online = true;
    let cycleState: EventStreamCycleState = 'stopped';

    function setCycleState(state: EventStreamCycleState): void {
        if (cycleState === state) {
            return;
        }
        cycleState = state;
        options.setCycleState(state);
    }

    function isCurrent(controller: AbortController): boolean {
        return running && !stoppedByFailure && activeController === controller;
    }

    function notifyFailure(failure: EventStreamClientFailure): void {
        options.recordIssue(failure);
        options.handoffFailure(failure);
    }

    function cancelTimer(): void {
        cancelScheduledTask?.();
        cancelScheduledTask = null;
        scheduleVersion += 1;
    }

    function releaseBrowserSubscriptions(): void {
        unsubscribeVisibility?.();
        unsubscribeVisibility = null;
        unsubscribeOnline?.();
        unsubscribeOnline = null;
    }

    function abandonRequest(controller: AbortController): void {
        const publishPartialCycle =
            activeCycleReadState === 'reading_without_done';
        activeController = null;
        activeCycleReadState = 'opening';
        controller.abort();
        if (publishPartialCycle) {
            options.publishCycle();
            publishedCursor = acknowledgedCursor;
            return;
        }
        // The harness may already hold a `done` that EOF has not proven.
        // Publishing would mark catch-up complete, so rewind and let the server
        // resend the accepted prefix from the last published cursor.
        acknowledgedCursor = publishedCursor;
    }

    function stopForFailure(
        controller: AbortController,
        failure: EventStreamClientFailure,
    ): void {
        if (!isCurrent(controller)) {
            return;
        }
        stoppedByFailure = true;
        running = false;
        cancelTimer();
        releaseBrowserSubscriptions();
        abandonRequest(controller);
        setCycleState('stopped');
        notifyFailure(failure);
    }

    function scheduleTask(callback: () => void, delayMs: number | null): void {
        activeController = null;
        activeCycleReadState = 'opening';
        scheduleVersion += 1;
        const version = scheduleVersion;
        const guardedCallback = () => {
            if (!running || stoppedByFailure || scheduleVersion !== version) {
                return;
            }
            cancelScheduledTask = null;
            callback();
        };
        cancelScheduledTask =
            delayMs === null
                ? options.queueMicrotask(guardedCallback)
                : options.schedule(guardedCallback, delayMs);
    }

    function networkDelayMs(): number {
        const lastIndex = NETWORK_RETRY_BASES_MS.length - 1;
        // The clamp guarantees a hit; the coalesce only satisfies
        // noUncheckedIndexedAccess without a second copy of the plateau value.
        const baseMs =
            NETWORK_RETRY_BASES_MS[Math.min(networkRetryIndex, lastIndex)] ??
            NETWORK_RETRY_PLATEAU_MS;
        networkRetryIndex += 1;
        return Math.round(baseMs * options.jitterFactor());
    }

    function recoverNetwork(
        controller: AbortController,
        failure: EventStreamClientFailure,
        delayMs: number | null = null,
        cycleState: 'retrying' | 'rate-limited' = 'retrying',
    ): void {
        if (!isCurrent(controller)) {
            return;
        }
        // The backoff would otherwise keep reporting the request that just
        // failed, so the harness could not tell a pause from a live read.
        setCycleState(cycleState);
        abandonRequest(controller);
        scheduleTask(beginRequest, delayMs ?? networkDelayMs());
        options.recordIssue(failure);
    }

    function recoverProtocol(
        controller: AbortController,
        failure: ProtocolFailure,
        observedFailure: EventStreamClientFailure,
    ): void {
        if (!isCurrent(controller)) {
            return;
        }
        const cursor = failure.acknowledgedCursor;
        const fingerprint: ProtocolFingerprint = [
            failure.reason,
            failure.lineType,
            failure.eventType,
            cursor.after_offset,
            cursor.after_pending_created_at,
            cursor.after_pending_id,
            cursor.after_pending_status_updated_at,
            cursor.after_pending_status_id,
        ];
        const repeated =
            replayedProtocolFingerprint !== null &&
            replayedProtocolFingerprint.every(
                (part, index) => part === fingerprint[index],
            );
        if (repeated) {
            stopForFailure(controller, observedFailure);
            return;
        }
        abandonRequest(controller);
        replayedProtocolFingerprint = fingerprint;
        setCycleState('replaying');
        scheduleTask(beginRequest, PROTOCOL_REPLAY_DELAY_MS);
        notifyFailure(observedFailure);
    }

    function handleHttpFailure(
        controller: AbortController,
        error: RunEventStreamHttpError,
    ): void {
        const policy = httpFailurePolicy(error.status);
        const failure: EventStreamClientFailure = {
            kind: 'http',
            reason: policy.reason,
            status: error.status,
            acknowledgedCursor,
        };
        switch (policy.recovery) {
            case 'rate_limited': {
                const retryAfterMs = error.retryAfterMs;
                const delayMs =
                    retryAfterMs !== null && Number.isInteger(retryAfterMs)
                        ? Math.min(
                              Math.max(retryAfterMs, MIN_RETRY_AFTER_MS),
                              MAX_RETRY_AFTER_MS,
                          )
                        : null;
                recoverNetwork(controller, failure, delayMs, 'rate-limited');
                return;
            }
            case 'retry':
                recoverNetwork(controller, failure);
                return;
            case 'stop':
                stopForFailure(controller, failure);
                return;
            default: {
                const exhaustiveRecovery: never = policy.recovery;
                throw new Error(
                    `Unknown HTTP recovery: ${String(exhaustiveRecovery)}`,
                );
            }
        }
    }

    function scheduleSuccessor(
        controller: AbortController,
        done: DoneLine,
        cycleStartCursor: StreamCursor,
        runStateChanged: boolean,
    ): void {
        const madeProgress =
            compareCursor(acknowledgedCursor, cycleStartCursor).some(
                (comparison) => comparison > 0,
            ) || runStateChanged;
        if (madeProgress) {
            consecutiveNoProgressCount = 0;
        }
        if (done.reason === 'page_full' && !madeProgress) {
            const failure = protocolFailure(
                'page_full_without_progress',
                acknowledgedCursor,
                done.type,
                null,
            );
            recoverProtocol(controller, failure, failure);
            return;
        }
        if (activeCycleReadState !== 'eof_proven') {
            throw new Error('Cannot publish an event stream cycle before EOF.');
        }
        networkRetryIndex = 0;
        replayedProtocolFingerprint = null;
        // Record the cursor before publishing: a throw from publishCycle must
        // not leave an abandon able to roll back past an already-published
        // cycle, which would drop its events into the resulting gap.
        publishedCursor = acknowledgedCursor;
        options.publishCycle();
        if (done.reason === 'cycle_complete') {
            options.onCycleComplete();
        }
        if (!isCurrent(controller)) {
            return;
        }
        switch (done.reason) {
            case 'page_full':
                setCycleState('draining');
                scheduleTask(() => scheduleTask(beginRequest, 0), null);
                return;
            case 'cycle_complete': {
                setCycleState('complete');
                if (madeProgress) {
                    scheduleTask(beginRequest, 100);
                    return;
                }
                consecutiveNoProgressCount += 1;
                const exponentialDelayMs = Math.min(
                    activityBaseMs(options.getActivityState()) *
                        2 ** (consecutiveNoProgressCount - 1),
                    MAX_DELAY_MS,
                );
                scheduleTask(
                    beginRequest,
                    Math.round(exponentialDelayMs * options.jitterFactor()),
                );
                return;
            }
            case 'idle_timeout':
                setCycleState('complete');
                scheduleTask(
                    beginRequest,
                    Math.round(
                        activityBaseMs(options.getActivityState()) *
                            options.jitterFactor(),
                    ),
                );
                return;
            default: {
                const exhaustiveReason: never = done.reason;
                throw new Error(
                    `Unknown event stream done reason: ${String(exhaustiveReason)}`,
                );
            }
        }
    }

    async function readRequest(controller: AbortController): Promise<void> {
        const cycleStartCursor = acknowledgedCursor;
        let responseOpened = false;
        let done: DoneLine | null = null;
        let runStateChanged = false;
        // A quiet cycle holds a finished run's state for the whole long-poll.
        let publishInertRunState = false;

        function parserFailure(problem: NdjsonParseProblem): void {
            const failure: EventStreamClientFailure = {
                kind: 'parser',
                reason: problem.kind,
                acknowledgedCursor,
            };
            let reason: ProtocolProblem['reason'];
            switch (problem.kind) {
                case 'eof_before_done':
                    reason = 'missing_done';
                    break;
                case 'invalid_utf8':
                case 'invalid_json':
                case 'invalid_record':
                case 'parser_already_failed':
                    reason = 'invalid_line';
                    break;
                default: {
                    const exhaustiveKind: never = problem.kind;
                    throw new Error(
                        `Unknown parser problem: ${String(exhaustiveKind)}`,
                    );
                }
            }
            recoverProtocol(
                controller,
                protocolFailure(reason, acknowledgedCursor),
                failure,
            );
        }

        function handleParsed(parsed: NdjsonParseResult): boolean {
            if (!acceptLines(parsed.lines)) {
                return false;
            }
            if (!parsed.ok) {
                parserFailure(parsed.problem);
                return false;
            }
            return true;
        }

        function acceptLines(lines: ReadonlyArray<EventStreamLine>): boolean {
            for (const line of lines) {
                if (line.type === 'done') {
                    activeCycleReadState = 'done_before_eof';
                    const problem = doneCursorProblem(
                        line.cursor,
                        acknowledgedCursor,
                    );
                    if (problem !== null) {
                        const failure = protocolFailure(
                            problem,
                            acknowledgedCursor,
                            line.type,
                            null,
                        );
                        recoverProtocol(controller, failure, failure);
                        return false;
                    }
                    done = line;
                    continue;
                }

                let result: EventStreamIngestionResult;
                try {
                    result = options.ingestLine(line);
                } catch {
                    const eventType =
                        line.type === 'event'
                            ? line.event.type
                            : line.type === 'pending_event'
                              ? line.pending_event.type
                              : line.type === 'pending_event_update'
                                ? line.pending_event_update.type
                                : null;
                    result = {
                        ok: false,
                        problem: {
                            reason: 'unexpected_exception',
                            lineType: line.type,
                            eventType,
                        },
                    };
                }
                if (!result.ok) {
                    const failure = protocolFailure(
                        result.problem.reason,
                        acknowledgedCursor,
                        result.problem.lineType,
                        result.problem.eventType,
                    );
                    recoverProtocol(controller, failure, failure);
                    return false;
                }
                if (line.type === 'run_state' && result.runStateChanged) {
                    runStateChanged = true;
                    if (
                        !line.run_state.is_generating &&
                        !line.run_state.has_active_lease &&
                        !line.run_state.has_pending_work
                    ) {
                        publishInertRunState = true;
                    }
                }
                const cursorAdvanced = compareCursor(
                    result.acknowledgedCursor,
                    acknowledgedCursor,
                ).some((comparison) => comparison > 0);
                acknowledgedCursor = result.acknowledgedCursor;
                if (cursorAdvanced) {
                    replayedProtocolFingerprint = null;
                }
            }
            return true;
        }

        try {
            const includeSnapshot = firstRequest;
            firstRequest = false;
            const response = await options.openEventStream({
                scope: options.scope,
                cursor: acknowledgedCursor,
                includeSnapshot,
                maxEventCount: 500,
                maxPendingEventCount: 100,
                maxPendingStatusUpdateCount: 100,
                waitMs: includeSnapshot ? 0 : 25_000,
                signal: controller.signal,
            });
            if (!isCurrent(controller)) {
                return;
            }
            responseOpened = true;
            activeCycleReadState = 'reading_without_done';
            const parser = createNdjsonParser(options.classifyLine);
            const reader = response.body?.getReader();
            if (reader === undefined) {
                throw new Error('Stream response body is missing.');
            }
            while (true) {
                const read = await reader.read();
                if (!isCurrent(controller)) {
                    return;
                }
                if (read.done) {
                    break;
                }
                if (!handleParsed(parser.push(read.value))) {
                    return;
                }
                if (publishInertRunState && done === null) {
                    // Abandoning publishes the partial cycle and its cursor.
                    publishInertRunState = false;
                    abandonRequest(controller);
                    scheduleTask(beginRequest, 0);
                    return;
                }
            }
            if (!handleParsed(parser.finish())) {
                return;
            }
            if (done === null) {
                throw new Error('Parser finished without a done line.');
            }
            activeCycleReadState = 'eof_proven';
            scheduleSuccessor(
                controller,
                done,
                cycleStartCursor,
                runStateChanged,
            );
        } catch (error: unknown) {
            if (!isCurrent(controller)) {
                return;
            }
            if (error instanceof RunEventStreamHttpError) {
                handleHttpFailure(controller, error);
                return;
            }
            if (error instanceof RunEventStreamProtocolError) {
                const failure = protocolFailure(
                    'unexpected_exception',
                    acknowledgedCursor,
                );
                recoverProtocol(controller, failure, failure);
                return;
            }
            recoverNetwork(controller, {
                kind: 'transport',
                reason: responseOpened ? 'read_failed' : 'open_failed',
                acknowledgedCursor,
            });
        }
    }

    function beginRequest(): void {
        if (
            !running ||
            stoppedByFailure ||
            !visible ||
            activeController !== null
        ) {
            return;
        }
        const controller = new AbortController();
        activeController = controller;
        activeCycleReadState = 'opening';
        cancelScheduledTask = null;
        setCycleState('draining');
        void readRequest(controller);
    }

    function pauseRead(): void {
        cancelTimer();
        const controller = activeController;
        if (controller !== null) {
            abandonRequest(controller);
        }
        setCycleState('hidden');
    }

    function handleVisibilityChange(): void {
        if (!running || stoppedByFailure) {
            return;
        }
        const nextVisible = options.getVisible();
        if (nextVisible === visible) {
            return;
        }
        visible = nextVisible;
        options.foregroundClock.setVisible(visible);
        if (!visible) {
            pauseRead();
            return;
        }
        beginRequest();
    }

    // `navigator.onLine` is a hint, never a gate: the HTML spec calls it
    // "inherently unreliable", and a false reading over a working network
    // never clears, because the browser fires no event. So connectivity may
    // shorten a wait, never start or stop one.
    function handleOnlineChange(): void {
        if (!running || stoppedByFailure) {
            return;
        }
        const nextOnline = options.getOnline();
        if (nextOnline === online) {
            return;
        }
        online = nextOnline;
        // A rate-limit wait is the server's instruction, not our backoff, so
        // a browser hint must not cut it short and re-add 429 load.
        if (
            !online ||
            activeController !== null ||
            cycleState === 'rate-limited'
        ) {
            return;
        }
        cancelTimer();
        beginRequest();
    }

    return {
        start(): void {
            if (running) {
                return;
            }
            running = true;
            stoppedByFailure = false;
            replayedProtocolFingerprint = null;
            visible = options.getVisible();
            online = options.getOnline();
            options.foregroundClock.setVisible(visible);
            unsubscribeVisibility = options.subscribeVisibility(
                handleVisibilityChange,
            );
            unsubscribeOnline = options.subscribeOnline(handleOnlineChange);
            if (!visible) {
                setCycleState('hidden');
                return;
            }
            beginRequest();
        },
        stop(): void {
            if (!running) {
                return;
            }
            running = false;
            stoppedByFailure = false;
            cancelTimer();
            releaseBrowserSubscriptions();
            const controller = activeController;
            if (controller !== null) {
                abandonRequest(controller);
            }
            setCycleState('stopped');
        },
    };
}
